import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useBalance } from "../context/balanceContext";
import { useTransactions } from "../context/transactionContext";
import { useProfile } from "../context/profileContext";

/*
  Withdraw.jsx

  - Preserves all business logic (withdraw API call, status normalization, data fetching).
  - Visual/layout adjustments and small interactive enhancements:
    * Adds an inline network selector dropdown shown when the "Please select your network" row is clicked.
      - Dropdown contains sample networks (ERC-20 USDT, TRC-20 USDT, ERC-20 USDC, TRC-20 USDC).
      - Selecting a network updates the selected network shown in the form.
      - Clicking outside the dropdown closes it.
    * Keeps all existing behaviour (withdraw flow, context calls) unchanged.
    * The vertical blue rail that previously appeared next to the balance has been removed as requested.
    * No other parts of the file were removed or altered.
*/

const WITHDRAW_STATUSES = {
  reviewing: ["Pending", "Reviewing", "In Review"],
  success: ["Completed", "Success"],
  reject: ["Rejected", "Reject", "Failed"],
};
function normalizeStatus(status) {
  if (!status) return "reviewing";
  if (WITHDRAW_STATUSES.reviewing.some(s => status.toLowerCase().includes(s.toLowerCase()))) return "reviewing";
  if (WITHDRAW_STATUSES.success.some(s => status.toLowerCase().includes(s.toLowerCase()))) return "success";
  if (WITHDRAW_STATUSES.reject.some(s => status.toLowerCase().includes(s.toLowerCase()))) return "reject";
  if (status.toLowerCase().includes("approved")) return "success";
  return "reviewing";
}

const START_BLUE = "#0b62ff";

export default function Withdraw() {
  const [tab] = useState("withdraw"); // kept for parity; only 'withdraw' used in UI
  const [amount, setAmount] = useState("");
  const [withdrawPassword, setWithdrawPassword] = useState("");
  const [message, setMessage] = useState("");
  const navigate = useNavigate();

  const { balance, refreshProfile } = useBalance();
  const { withdrawals, loading, refresh } = useTransactions();
  const { profile } = useProfile();

  // network selector state
  const [networks] = useState([
    "ERC-20 USDT",
    "TRC-20 USDT",
    "ERC-20 USDC",
    "TRC-20 USDC",
    "BEP-20 USDT",
  ]);
  const [selectedNetwork, setSelectedNetwork] = useState(null);
  const [networkOpen, setNetworkOpen] = useState(false);
  const networkRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (
        networkOpen &&
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        networkRef.current &&
        !networkRef.current.contains(e.target)
      ) {
        setNetworkOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [networkOpen]);

  const handleWithdraw = async (e) => {
    e.preventDefault();
    setMessage("");
    if (!amount || Number(amount) <= 0) {
      setMessage("Please enter a valid amount.");
      return;
    }
    if (!withdrawPassword) {
      setMessage("Please enter your withdrawal password.");
      return;
    }
    if (!selectedNetwork) {
      setMessage("Please select a network.");
      return;
    }
    const token = localStorage.getItem("authToken");
    const BASE_URL = "https://digitalblits-admin.onrender.com";
    const res = await fetch(`${BASE_URL}/api/withdraw`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Auth-Token": token,
      },
      body: JSON.stringify({ amount, withdrawPassword, network: selectedNetwork }),
    });
    const data = await res.json();
    if (data.success) {
      setMessage("Withdrawal request submitted and is under review.");
      setAmount("");
      setWithdrawPassword("");
      setSelectedNetwork(null);
      setNetworkOpen(false);
      refresh();
      refreshProfile();
    } else {
      setMessage(data.message || "Failed to withdraw.");
    }
  };

  const maxCardWidth = 1100;

  // Filtering logic preserved but history UI is intentionally not shown per request
  const filteredWithdrawals = (withdrawals || []).filter(
    w => normalizeStatus(w.status) === "reviewing"
  );

  const formattedBalance = Number(balance || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  const frozenAmount = (profile && (profile.frozenAmount || profile.frozen)) || 0;
  const formattedFrozen = Number(frozenAmount || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return (
    <main style={{ minHeight: "100vh", position: "relative", overflowX: "hidden", background: "#fff" }}>
      <style>{`
        /* Page wrapper: centered and responsive */
        .withdraw-page {
          max-width: 1200px;
          margin: 0 auto;
          padding: 22px 18px 80px;
          box-sizing: border-box;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial;
          color: #111;
        }

        /* Header */
        .withdraw-header {
          margin-bottom: 12px;
        }
        .withdraw-title {
          font-size: 48px;
          font-weight: 900;
          color: ${START_BLUE};
          margin: 6px 0 6px 0;
          line-height: 1;
        }
        .withdraw-sub {
          font-size: 13px;
          font-weight: 700;
          color: #111;
          letter-spacing: 0.02em;
        }

        /* thin separator under header */
        .withdraw-sep {
          height: 1px;
          background: #efefef;
          margin: 18px 0 10px;
          border: none;
        }

        /* Balance / stats area: layout uses grid so frozen box can sit at right on desktop */
        .balance-grid {
          display: grid;
          grid-template-columns: 1fr 320px;
          gap: 18px;
          align-items: start;
          margin-bottom: 28px;
          padding-top: 6px;
          padding-bottom: 18px;
        }

        /* Left: big balance row */
        .balance-row {
          display: flex;
          gap: 18px;
          align-items: center;
        }

        /* NOTE: the blue vertical rail has been removed as requested */

        .balance-meta {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
        }
        .balance-label {
          font-size: 12px;
          color: #9aa7b6;
          font-weight: 700;
          text-transform: uppercase;
          margin-bottom: 6px;
        }
        .balance-value {
          font-size: 56px;
          font-weight: 900;
          color: ${START_BLUE};
        }

        /* Right: frozen balance card */
        .frozen-card {
          background: #fff;
          border-radius: 8px;
          padding: 20px;
          border: 1px solid #eee;
          box-shadow: 0 6px 18px rgba(2,6,23,0.04);
          display: flex;
          align-items: center;
          justify-content: center;
          flex-direction: column;
        }
        .frozen-value {
          color: ${START_BLUE};
          font-weight: 900;
          font-size: 28px;
          margin-bottom: 8px;
        }
        .frozen-label { color: #9aa7b6; font-weight: 700; font-size: 12px; text-transform: uppercase; }

        /* Withdrawal details header */
        .details-title {
          text-align: center;
          font-size: 26px;
          font-weight: 800;
          margin: 18px 0 18px;
        }

        /* Form card */
        .form-card {
          background: #efe9e5; /* beige */
          border-radius: 6px;
          padding: 26px;
          max-width: 900px;
          margin: 0 auto 18px;
          box-sizing: border-box;
          position: relative;
        }
        .form-row { margin-bottom: 14px; position: relative; }

        .label-muted {
          font-size: 13px;
          color: #666;
          margin-bottom: 8px;
          display: block;
        }

        .input-box {
          width: 100%;
          padding: 12px 14px;
          border-radius: 6px;
          border: 1px solid #e0dcd9;
          background: #fff;
          font-size: 15px;
          color: #222;
          box-sizing: border-box;
        }

        /* Network selector row (styled like a button with chevron) */
        .network-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px 14px;
          border-radius: 6px;
          background: #fff;
          border: 1px solid #e0dcd9;
          cursor: pointer;
        }
        .network-placeholder { color: #333; font-size: 15px; }

        /* Dropdown styling */
        .network-dropdown {
          position: absolute;
          left: 12px;
          right: 12px;
          top: calc(100% + 8px);
          background: #fff;
          border-radius: 8px;
          box-shadow: 0 14px 40px rgba(2,6,23,0.12);
          border: 1px solid #e8e8e8;
          z-index: 1200;
          max-height: 220px;
          overflow-y: auto;
        }
        .network-item {
          padding: 14px 16px;
          font-size: 16px;
          color: #111;
          cursor: pointer;
          border-bottom: 1px solid #f4f4f4;
        }
        .network-item:last-child { border-bottom: none; }
        .network-item:hover { background: #fafafa; }

        /* password input with icon */
        .password-row {
          position: relative;
        }
        .password-eye {
          position: absolute;
          right: 12px;
          top: 70%;
          transform: translateY(-50%);
          cursor: pointer;
          color: #9aa7b6;
        }

        /* Terms link */
        .terms-row {
          text-align: center;
          margin: 8px 0 14px;
          font-size: 13px;
          color: #444;
        }
        .terms-row a { color: ${START_BLUE}; font-weight: 700; text-decoration: underline; }

        /* Submit button */
        .withdraw-action {
          width: 100%;
          display: flex;
          justify-content: center;
          margin-top: 8px;
        }
        .withdraw-btn {
          background: ${START_BLUE};
          color: #fff;
          border: none;
          padding: 16px 36px;
          border-radius: 999px;
          font-weight: 700;
          font-size: 16px;
          width: 100%;
          max-width: 760px;
          cursor: pointer;
          box-shadow: 0 10px 36px rgba(11,98,255,0.18);
        }

        /* Links bar (small) below form - shown to match screenshot */
        .links-bar {
          margin-top: 18px;
          padding: 12px 0;
          border-top: 1px solid #efefef;
          display: flex;
          justify-content: center;
          gap: 36px;
          color: #666;
          font-size: 13px;
        }

        /* Footer spacer */
        .footer-space { height: 36px; }

        /* Responsive rules */
        @media (max-width: 900px) {
          .withdraw-title { font-size: 40px; }
          .balance-grid { grid-template-columns: 1fr; }
          .frozen-card { order: 2; margin-top: 12px; }
          .balance-value { font-size: 40px; }
          .form-card { padding: 20px; margin-left: 6px; margin-right: 6px; }
          .withdraw-page { padding-bottom: 40px; }
          .network-dropdown { left: 6px; right: 6px; top: calc(100% + 6px); }
        }
      `}</style>

      <div className="withdraw-page" role="main" aria-labelledby="withdraw-heading">
        <header className="withdraw-header" aria-hidden="false">
          <h1 id="withdraw-heading" className="withdraw-title">WITHDRAWAL</h1>
          <div className="withdraw-sub">CASH OUT YOUR CREDIT BALANCE ANYTIME</div>
        </header>

        <hr className="withdraw-sep" />

        {/* Balance grid: left main balance, right frozen card */}
        <div className="balance-grid" aria-hidden="false">
          <div>
            <div className="balance-row" role="region" aria-label="Available amount">
              <div className="balance-meta">
                <div className="balance-label">Available amount </div>
                <div className="balance-value" aria-live="polite">{formattedBalance}</div>
              </div>
            </div>
          </div>

          <div>
            <div className="frozen-card" role="status" aria-label="Frozen balance">
              <div className="frozen-value">{formattedFrozen}</div>
              <div className="frozen-label">Frozen balance </div>
            </div>
          </div>
        </div>

        {/* Withdrawal details */}
        <h2 className="details-title">Withdrawal Details</h2>

        <section className="form-card" aria-label="Withdrawal form">
          <form onSubmit={handleWithdraw} autoComplete="off" style={{ display: "block" }}>
            <div className="form-row">
              <label className="label-muted">Withdrawal Amount</label>
              <input
                className="input-box"
                type="text"
                inputMode="decimal"
                placeholder="00.00"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            </div>

            <div className="form-row" ref={networkRef}>
              <label className="label-muted">Network</label>
              <div
                className="network-row"
                role="button"
                tabIndex={0}
                onClick={() => setNetworkOpen((s) => !s)}
                onKeyDown={(e) => { if (e.key === "Enter") setNetworkOpen((s) => !s); }}
                aria-haspopup="listbox"
                aria-expanded={networkOpen}
                aria-controls="network-list"
              >
                <div className="network-placeholder">{selectedNetwork || "Please select your network"}</div>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M9 6l6 6-6 6" stroke="#9aa7b6" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>

              {networkOpen && (
                <div
                  id="network-list"
                  ref={dropdownRef}
                  className="network-dropdown"
                  role="listbox"
                  aria-label="Available networks"
                >
                  {networks.map((n) => (
                    <div
                      key={n}
                      role="option"
                      aria-selected={n === selectedNetwork}
                      className="network-item"
                      onClick={() => {
                        setSelectedNetwork(n);
                        setNetworkOpen(false);
                        setMessage("");
                      }}
                      onKeyDown={(e) => { if (e.key === "Enter") { setSelectedNetwork(n); setNetworkOpen(false); } }}
                      tabIndex={0}
                    >
                      {n}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="form-row password-row">
              <label className="label-muted">Secondary Password</label>
              <input
                className="input-box"
                type="password"
                placeholder="Your secondary password"
                value={withdrawPassword}
                onChange={(e) => setWithdrawPassword(e.target.value)}
              />
              <div
                className="password-eye"
                title="Toggle visibility"
                onClick={() => {
                  const el = document.querySelector('.form-card input[type="password"]');
                  if (!el) return;
                  el.type = el.type === "password" ? "text" : "password";
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12z" stroke="#9aa7b6" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                  <circle cx="12" cy="12" r="3" stroke="#9aa7b6" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </div>

            <div className="terms-row">
              Learn more about <a href="/terms" target="_blank" rel="noreferrer">Terms and Conditions</a>
            </div>

            <div className="withdraw-action">
              <button type="submit" className="withdraw-btn">Withdraw</button>
            </div>

            {message && (
              <div style={{ marginTop: 14, color: "#333", textAlign: "center", fontWeight: 700 }} role="status">
                {message}
              </div>
            )}
          </form>
        </section>

        <div className="footer-space" />
      </div>
    </main>
  );
}
